import type { Point } from '../types';

/**
 * Calculate the Euclidean distance between two points
 */
export const distance = (p1: Point, p2: Point): number => {
  return Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
};

/**
 * Subtract two points (p1 - p2)
 */
export const subtractPoints = (p1: Point, p2: Point): Point => {
  return { x: p1.x - p2.x, y: p1.y - p2.y };
};

/**
 * Add two points (p1 + p2)
 */
export const addPoints = (p1: Point, p2: Point): Point => {
  return { x: p1.x + p2.x, y: p1.y + p2.y };
};

/**
 * Scale a point by a scalar value
 */
export const scalePoint = (point: Point, scalar: number): Point => {
  return { x: point.x * scalar, y: point.y * scalar };
};

/**
 * Calculate the dot product of two points (treated as vectors)
 */
export const dotProductPoints = (p1: Point, p2: Point): number => {
  return p1.x * p2.x + p1.y * p2.y;
};

/**
 * Normalize a point to unit length
 */
export const normalizePoint = (point: Point): Point => {
  const magnitude = Math.sqrt(point.x * point.x + point.y * point.y);
  if (magnitude === 0) {
    return { x: 0, y: 0 };
  }
  return { x: point.x / magnitude, y: point.y / magnitude };
};

/**
 * Calculate the cross product of two 2D vectors (returns scalar)
 */
export const crossProduct2D = (p1: Point, p2: Point): number => {
  return p1.x * p2.y - p1.y * p2.x;
};

/**
 * Calculate the magnitude (length) of a vector
 */
export const magnitude = (point: Point): number => {
  return Math.sqrt(point.x * point.x + point.y * point.y);
};

/**
 * Rotate a point around the origin by the given angle (in radians)
 */
export const rotatePoint = (point: Point, angleRadians: number): Point => {
  const cos = Math.cos(angleRadians);
  const sin = Math.sin(angleRadians);
  return {
    x: point.x * cos - point.y * sin,
    y: point.x * sin + point.y * cos
  };
};

/**
 * Get the angle between two points (in radians)
 */
export const angleBetweenPoints = (p1: Point, p2: Point): number => {
  return Math.atan2(p2.y - p1.y, p2.x - p1.x);
}; 